import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  RefreshCw, CheckCircle, XCircle, Loader2, Clock,
  Check, X, Zap, FileText, Eye, Plus, Delete, Save, Trash2,
  Repeat, Settings, Calendar, ToggleLeft, ToggleRight, Pencil,
  Printer, Download, Mail, AlertTriangle, Search, Gauge
} from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { documentsApi, recurringApi, opsApi } from '../../utils/api';
import useModulosAvanzados from '../../hooks/useModulosAvanzados';
import OpsResumenModal from '../../components/OpsResumenModal';
import useAuthStore from '../../store/authStore';
import InvoiceTemplate from '../../components/InvoiceTemplate';
import DraggableModal from '../../components/DraggableModal';
import CrearPlantillaRecurrenteModal from '../../components/CrearPlantillaRecurrenteModal';

// Estados
const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: 'bg-gray-100 text-gray-700', icon: FileText },
  pendiente: { label: 'Pendiente Facturar', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  pending: { label: 'Enviado', color: 'bg-blue-100 text-blue-700', icon: Clock },
  processing: { label: 'Procesando', color: 'bg-blue-100 text-blue-700', icon: RefreshCw },
  accepted: { label: 'Aceptada', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rechazada', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const TIPO_DOC = {
  '01': 'FE',
  '02': 'ND',
  '03': 'NC',
  '04': 'TE',
};

const formatCurrency = (amount) => {
  const num = Number(amount) || 0;
  return num.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Modal de confirmación
function ConfirmarModal({ isOpen, onClose, onConfirm, titulo, mensaje, loading }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={onClose} />
        <div className="relative w-full max-w-md p-6 bg-white rounded-2xl shadow-xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-100 rounded-full">
              <Delete className="h-6 w-6 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{titulo}</h3>
          </div>
          <p className="text-gray-600 mb-6">{mensaje}</p>
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Delete className="h-5 w-5" />}
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Modal de progreso SSE - Profesional con etapas (outline style)
function ProgresoModal({ isOpen, onClose, resultados, progreso, total, stage, completado, onErrorClick }) {
  if (!isOpen) return null;

  const porcentaje = total > 0 ? Math.round((progreso / total) * 100) : 0;
  const exitosos = resultados.filter(r => r.success).length;
  const fallidos = resultados.filter(r => !r.success).length;

  // Etapas del proceso de facturación
  const STAGES = {
    loading: { label: 'Cargando factura', icon: FileText, color: 'text-blue-600' },
    generating: { label: 'Generando XML', icon: FileText, color: 'text-purple-600' },
    signing: { label: 'Firmando documento', icon: Check, color: 'text-indigo-600' },
    submitting: { label: 'Enviando a Hacienda', icon: Zap, color: 'text-orange-600' },
    validating: { label: 'Validando respuesta', icon: RefreshCw, color: 'text-cyan-600' }
  };

  const currentStage = stage?.stage ? STAGES[stage.stage] : null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-gray-900/60" />
        <div className="relative w-full max-w-5xl bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden">
          {/* Header - outline style */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg border ${completado ? 'border-green-300 bg-green-50' : 'border-blue-300 bg-blue-50'}`}>
                {completado ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {completado ? 'Facturación Completada' : 'Procesando Facturas'}
                </h3>
                <p className="text-sm text-gray-500">
                  {completado
                    ? `${exitosos} exitosas, ${fallidos} con error`
                    : `Factura ${progreso} de ${total}`
                  }
                </p>
              </div>
              {completado && (
                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              )}
            </div>
          </div>

          <div className="p-6">
            {/* Barra de progreso */}
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="font-medium text-gray-700">Progreso</span>
                <span className="font-semibold text-gray-900">{porcentaje}%</span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                <div
                  className={`h-full transition-all duration-500 ease-out rounded-full ${
                    completado ? 'bg-green-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${porcentaje}%` }}
                />
              </div>
            </div>

            {/* Estado actual con etapas */}
            {!completado && currentStage && (
              <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2 rounded-lg border border-gray-200 ${currentStage.color}`}>
                    <currentStage.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{stage?.cliente || 'Procesando...'}</p>
                    <p className="text-sm text-gray-500">{currentStage.label}</p>
                  </div>
                </div>

                {/* Mini etapas */}
                <div className="flex items-center gap-1.5 mt-3">
                  {Object.entries(STAGES).map(([key], idx) => {
                    const stageKeys = Object.keys(STAGES);
                    const currentIdx = stageKeys.indexOf(stage?.stage);
                    const isActive = key === stage?.stage;
                    const isPast = idx < currentIdx;

                    return (
                      <div key={key} className="flex-1 flex items-center gap-1.5">
                        <div className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                          isPast ? 'bg-green-500' : isActive ? 'bg-blue-500' : 'bg-gray-200'
                        }`} />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-1.5 text-[10px] text-gray-400">
                  <span>XML</span>
                  <span>Firma</span>
                  <span>Envío</span>
                  <span>Validación</span>
                </div>
              </div>
            )}

            {/* Tabla de resultados */}
            {resultados.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                      <tr className="text-left text-[10px] font-semibold text-gray-600 tracking-wider">
                        <th className="px-3 py-2.5 w-24">Fecha</th>
                        <th className="px-3 py-2.5 w-44">Documento Electrónico</th>
                        <th className="px-3 py-2.5">Cliente</th>
                        <th className="px-3 py-2.5 text-center w-32">Estado Hacienda</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {resultados.map((r, idx) => (
                        <tr
                          key={idx}
                          className={`transition-colors ${
                            r.success
                              ? 'hover:bg-green-50/50'
                              : 'hover:bg-red-50/50'
                          }`}
                        >
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                            {new Date().toLocaleDateString('es-CR')}
                          </td>
                          <td className="px-3 py-2">
                            <code className="px-1.5 py-0.5 border border-gray-200 rounded text-[10px] font-mono text-gray-700">
                              {r.consecutivo || '—'}
                            </code>
                          </td>
                          <td className="px-3 py-2 text-gray-900 font-medium">
                            {r.cliente}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {r.estado === 'aceptado' || r.estado === 'accepted' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border border-green-300 text-green-700">
                                <CheckCircle className="w-3 h-3" />
                                Aceptado
                              </span>
                            ) : r.success && !r.error ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border border-amber-300 text-amber-700" title="Esperando la respuesta de Hacienda">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Procesando
                              </span>
                            ) : (
                              <button
                                onClick={() => onErrorClick?.(r)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border border-red-300 text-red-700 cursor-pointer hover:bg-red-50"
                                title="Click para ver detalle"
                              >
                                <XCircle className="w-3 h-3" />
                                Rechazado
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Botón cerrar */}
            {completado && (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Frecuencia labels
const FRECUENCIA_LABELS = {
  weekly: 'Semanal',
  biweekly: 'Quincenal',
  monthly: 'Mensual',
  quarterly: 'Trimestral',
  yearly: 'Anual'
};

// Modal de preview solo lectura
// También la usa Finance360 Ops para ver el borrador real
// `ajustarLineas` (opcional): Finance360 Ops muestra la factura como quedará con su borrador (cantidades,
// montos y líneas nuevas) sin tocar la FE guardada. `titulo` cambia el encabezado.
export function PreviewFacturaModal({ docId, plantillaId, emisor, onClose, ajustarLineas, titulo }) {
  const [loading, setLoading] = useState(true);
  const [docData, setDocData] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (docId) {
          const res = await documentsApi.getById(docId);
          const d = res.data?.data || res.data;
          setDocData(ajustarLineas && d ? { ...d, lineas: ajustarLineas(d.lineas || []) } : d);
        } else if (plantillaId) {
          const res = await recurringApi.getById(plantillaId);
          const p = res.data?.data || res.data;
          // Adaptar plantilla a formato de documento para InvoiceTemplate
          setDocData({
            consecutivo: 'PLANTILLA',
            tipo_documento: p.tipoDocumento || '01',
            fecha_emision: new Date().toISOString(),
            condicion_venta: p.condicionVenta || '01',
            medio_pago: p.medioPago || ['01'],
            codigo_moneda: p.codigoMoneda || 'CRC',
            status: 'draft',
            notas: p.notas || '',
            receptor_nombre: p.receptorNombre || p.receptor?.nombre,
            receptor_identificacion: p.receptorIdentificacion || p.receptor?.identificacion,
            receptor_tipo_identificacion: p.receptorTipoIdentificacion || p.receptor?.tipoIdentificacion || '02',
            receptor_email: p.receptorEmail || p.receptor?.email,
            receptor_telefono_numero: p.receptorTelefono || p.receptor?.telefonoNumero,
            receptor_otras_senas: p.receptorDireccion || p.receptor?.otrasSenas,
            receptor_dias_credito: p.diasCredito || 30,
            periodo_cobro_texto: p.periodoCobroTexto,
            lineas: (p.lineas || []).map((l, i) => ({
              numeroLinea: i + 1,
              codigo: l.codigo || '',
              codigoComercial: l.codigoComercial || l.codigo_comercial || '',
              sku: l.codigoComercial || l.codigo_comercial || '',
              detalle: l.detalle || l.descripcion || '',
              cantidad: Number(l.cantidad) || 1,
              unidadMedida: l.unidadMedida || 'Unid',
              precioUnitario: Number(l.precioUnitario || l.precio) || 0,
              impuestoTarifa: Number(l.impuestoTarifa || l.iva) || 0,
              impuestoExoneracion: l.impuestoExoneracion || l.exoneracion,
            })),
          });
        }
      } catch (error) {
        toast.error('Error al cargar preview');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [docId, plantillaId]);

  if (!docData && !loading) return null;

  const moneda = docData?.codigo_moneda || docData?.codigoMoneda || 'CRC';

  const emisorForTemplate = {
    nombre: emisor?.nombre || emisor?.name,
    nombre_comercial: emisor?.nombre_comercial || emisor?.nombreComercial,
    identificacion: emisor?.identificacion,
    tipo_identificacion: emisor?.tipo_identificacion || emisor?.tipoIdentificacion || '02',
    email: emisor?.email,
    telefono_codigo: emisor?.telefono_codigo || '506',
    telefono_numero: emisor?.telefono_numero || emisor?.telefonoNumero,
    provincia: emisor?.provincia,
    canton: emisor?.canton,
    distrito: emisor?.distrito,
    otras_senas: emisor?.otras_senas || emisor?.otrasSenas,
    logo_url: emisor?.logo_url || emisor?.logoUrl,
    template_color: emisor?.template_color || emisor?.templateColor || '#F47920',
    terminos_condiciones: emisor?.terminos_condiciones || emisor?.terminosCondiciones
  };

  const receptorForTemplate = {
    nombre: docData?.receptor_nombre || docData?.receptorNombre,
    identificacion: docData?.receptor_identificacion || docData?.receptorIdentificacion,
    tipo_identificacion: docData?.receptor_tipo_identificacion || docData?.receptorTipoIdentificacion || '02',
    email: docData?.receptor_email || docData?.receptorEmail,
    telefono_numero: docData?.receptor_telefono_numero || docData?.receptorTelefono,
    otras_senas: docData?.receptor_otras_senas || docData?.receptorOtrasSenas,
    dias_credito: docData?.receptor_dias_credito || docData?.receptorDiasCredito || 30
  };

  const documentoForTemplate = {
    consecutivo: docData?.consecutivo || 'BORRADOR',
    clave: docData?.clave,
    tipoDocumento: docData?.tipo_documento || docData?.tipoDocumento || '01',
    fechaEmision: docData?.fecha_emision || docData?.fechaEmision || new Date().toISOString(),
    condicionVenta: docData?.condicion_venta || docData?.condicionVenta || '01',
    medioPago: docData?.medio_pago || docData?.medioPago || ['01'],
    codigoMoneda: moneda,
    status: docData?.status || 'draft',
    notas: docData?.notas || '',
    diasCredito: receptorForTemplate.dias_credito,
    periodoCobroTexto: docData?.periodo_cobro_texto || docData?.periodoCobroTexto
  };

  const lineasForTemplate = (docData?.lineas || []).map((l, idx) => {
    const cantidad = Number(l.cantidad) || 1;
    const precio = Number(l.precioUnitario || l.precio_unitario) || 0;
    const tarifa = Number(l.impuestoTarifa || l.impuesto_tarifa) || 0;
    const montoTotal = cantidad * precio;
    const exoneracion = l.impuestoExoneracion || l.impuesto_exoneracion;
    const porcentajeExo = exoneracion?.porcentaje || 0;
    const montoExonerado = porcentajeExo > 0 ? montoTotal * (Math.min(porcentajeExo, tarifa) / 100) : 0;
    const impuestoMonto = (montoTotal * tarifa / 100) - montoExonerado;
    return {
      numeroLinea: idx + 1,
      codigo: l.codigo || l.Codigo || '',
      codigoComercial: l.codigoComercial || l.codigo_comercial || '',
      sku: l.sku || l.codigoComercial || l.codigo_comercial || '',
      detalle: l.detalle || l.Detalle || '',
      cantidad, unidadMedida: l.unidadMedida || l.unidad_medida || 'Unid',
      precioUnitario: precio, montoTotal, descuentoMonto: 0, subtotal: montoTotal,
      impuestoTarifa: tarifa, impuestoMonto, impuestoExoneracion: exoneracion,
      montoTotalLinea: montoTotal + impuestoMonto
    };
  });

  const totalVenta = lineasForTemplate.reduce((s, l) => s + l.montoTotal, 0);
  const totalImpuesto = lineasForTemplate.reduce((s, l) => s + l.impuestoMonto, 0);
  const totalExonerado = lineasForTemplate.reduce((s, l) => {
    const exo = l.impuestoExoneracion;
    if (!exo?.porcentaje) return s;
    return s + l.montoTotal * (Math.min(exo.porcentaje, l.impuestoTarifa) / 100);
  }, 0);
  const totales = {
    totalVentaNeta: totalVenta,
    totalImpuesto,
    totalComprobante: totalVenta + totalImpuesto,
    totalExonerado
  };

  const handleDownloadPdf = () => {
    const printArea = document.getElementById('preview-print-area');
    if (!printArea) return;
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>Preview</title><style>
      body { margin: 0; padding: 8mm; font-family: Arial, sans-serif; }
      @media print { body { margin: 0; } }
    </style></head><body>${printArea.innerHTML}</body></html>`);
    win.document.close();
    setTimeout(() => { win.print(); win.close(); }, 500);
  };

  return (
    <DraggableModal title={titulo || (plantillaId ? 'Vista Previa - Plantilla' : 'Vista Previa - Factura')} onClose={onClose} maxWidth="max-w-4xl">
      <div className="flex items-center justify-end gap-2 px-4 pt-3">
        <button
          onClick={handleDownloadPdf}
          className="inline-flex items-center gap-2 px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 text-sm font-medium"
        >
          <Printer className="h-4 w-4" />
          Imprimir / PDF
        </button>
      </div>
      <div className="p-4 overflow-auto bg-gray-100" style={{ maxHeight: '75vh' }}>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
          </div>
        ) : (
          <div id="preview-print-area" className="bg-white w-full" style={{ padding: '8mm' }}>
            <InvoiceTemplate
              documento={documentoForTemplate}
              emisor={emisorForTemplate}
              receptor={receptorForTemplate}
              lineas={lineasForTemplate}
              totales={totales}
              esCotizacion={false}
              tipoCambio={1}
              fullWidth={true}
            />
          </div>
        )}
      </div>
    </DraggableModal>
  );
}

export default function FacturacionRecurrente() {
  // Get current emisor
  const { currentEmisor } = useAuthStore();
  // Finance360 Ops: el botón de análisis solo aparece con el módulo y solo en borradores de consumo
  const modulosAvanzados = useModulosAvanzados(currentEmisor?.id);
  const conOps = !!modulosAvanzados?.includes('ops');
  const [deConsumo, setDeConsumo] = useState(new Set());
  const [analizarId, setAnalizarId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab state
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return ['facturas', 'plantillas'].includes(tabFromUrl) ? tabFromUrl : 'facturas';
  });

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const [loading, setLoading] = useState(true);
  const [facturas, setFacturas] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [editandoDoc, setEditandoDoc] = useState(null);

  // Modal: configuración de destinatarios del reporte post-ciclo
  const [showReporteConfig, setShowReporteConfig] = useState(false);

  // Modal: progreso de aprobación masiva
  const [bulkApprove, setBulkApprove] = useState(null);
  const bulkApproveCancelRef = useRef(false);

  // Modal mensaje Hacienda (errores/rechazos)
  const [mensajeHaciendaModal, setMensajeHaciendaModal] = useState(null);

  // Modal eliminar
  const [eliminarDoc, setEliminarDoc] = useState(null);
  const [showDeleteBatchModal, setShowDeleteBatchModal] = useState(false);
  const [eliminando, setEliminando] = useState(false);

  // Modal SSE
  const [showProgresoModal, setShowProgresoModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [docsParaProcesar, setDocsParaProcesar] = useState([]);
  const [progreso, setProgreso] = useState(0);
  const [totalProcesar, setTotalProcesar] = useState(0);
  const [stageInfo, setStageInfo] = useState(null);
  const [resultados, setResultados] = useState([]);
  const [completado, setCompletado] = useState(false);
  const eventSourceRef = useRef(null);

  // Plantillas state
  const [plantillas, setPlantillas] = useState([]);
  const [loadingPlantillas, setLoadingPlantillas] = useState(false);
  const [editandoPlantilla, setEditandoPlantilla] = useState(null);
  const [eliminarPlantilla, setEliminarPlantilla] = useState(null);
  const [eliminandoPlantilla, setEliminandoPlantilla] = useState(false);
  const [showCrearPlantilla, setShowCrearPlantilla] = useState(false);
  const [selectedPlantillas, setSelectedPlantillas] = useState([]);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewPlantillaId, setPreviewPlantillaId] = useState(null);
  const [searchFacturas, setSearchFacturas] = useState('');
  const [searchPlantillas, setSearchPlantillas] = useState('');
  const [plantillaSubTab, setPlantillaSubTab] = useState('mensual'); // 'mensual' | 'anual'
  const [facturasSubTab, setFacturasSubTab] = useState('mensual'); // 'mensual' | 'anual'
  // Por omisión ambas listas salen ordenadas por cliente (A→Z); el usuario puede cambiar la columna o el sentido
  const [sortPlantillas, setSortPlantillas] = useState({ field: 'cliente', dir: 'asc' });
  const [sortFacturas, setSortFacturas] = useState({ field: 'cliente', dir: 'asc' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await documentsApi.list({ status: 'draft', recurring: true, limit: 100 });
      setFacturas(res.data?.data || []);
      // Solo las FE en borrador que ya recibieron una actualización de Finance360 Ops
      if (conOps) opsApi.conCambios().then(r => setDeConsumo(new Set(r.data?.data || []))).catch(() => setDeConsumo(new Set()));
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  }, [conOps]);

  const fetchPlantillas = useCallback(async () => {
    setLoadingPlantillas(true);
    try {
      const res = await recurringApi.list({ limit: 100 });
      setPlantillas(res.data?.data || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar plantillas');
    } finally {
      setLoadingPlantillas(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchPlantillas();
    return () => eventSourceRef.current?.close();
  }, [fetchData, fetchPlantillas]);

  // Toggle plantilla activa/inactiva
  const handleTogglePlantilla = async (plantilla) => {
    try {
      if (plantilla.isActive) {
        await recurringApi.deactivate(plantilla.id);
        toast.success('Plantilla desactivada');
      } else {
        await recurringApi.activate(plantilla.id);
        toast.success('Plantilla activada');
      }
      fetchPlantillas();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  // Eliminar plantilla
  const handleEliminarPlantillaConfirm = async () => {
    if (!eliminarPlantilla) return;
    setEliminandoPlantilla(true);
    try {
      await recurringApi.delete(eliminarPlantilla.id);
      toast.success('Plantilla eliminada');
      setEliminarPlantilla(null);
      fetchPlantillas();
    } catch (error) {
      toast.error('Error al eliminar plantilla');
    } finally {
      setEliminandoPlantilla(false);
    }
  };

  // Generar factura manualmente desde plantilla
  const handleGenerarManual = async (plantilla) => {
    try {
      toast.loading('Generando factura...', { id: 'generating' });
      await recurringApi.generate(plantilla.id, false); // false = no enviar a Hacienda, solo borrador
      toast.success('Factura generada como borrador', { id: 'generating' });
      fetchData(); // Recargar facturas
      fetchPlantillas(); // Recargar plantillas (actualiza contador)
      // Stay on current tab - don't force tab change
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al generar factura', { id: 'generating' });
    }
  };

  const pendientes = facturas.filter(f => f.status === 'draft' && f.metadata?.aprobado_recurrente);

  // Filtered lists for search
  const filteredFacturas = facturas.filter(f => {
    if (!searchFacturas) return true;
    const q = searchFacturas.toLowerCase();
    return (f.receptor_nombre || f.receptorNombre || '').toLowerCase().includes(q)
      || (f.consecutivo || '').toLowerCase().includes(q)
      || (f.receptor_identificacion || '').toLowerCase().includes(q);
  });

  // Facturas visibles (filtradas por sub-tab)
  const visibleFacturas = filteredFacturas.filter(f => {
    const pid = f.metadata?.recurringInvoiceId;
    const p = plantillas.find(pl => pl.id === pid);
    return facturasSubTab === 'anual' ? (p && p.frecuencia === 'yearly') : (!p || p.frecuencia !== 'yearly');
  });

  const toggleSortPlantillas = (field) => {
    setSortPlantillas(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  };
  const toggleSortFacturas = (field) => {
    setSortFacturas(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  };

  const sortList = (list, sort, getField) => {
    if (!sort.field) return list;
    return [...list].sort((a, b) => {
      const av = (getField(a, sort.field) || '').toString().trim();
      const bv = (getField(b, sort.field) || '').toString().trim();
      const cmp = av.localeCompare(bv, 'es', { sensitivity: 'base', numeric: true });
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  };

  const filteredPlantillas = plantillas.filter(p => {
    if (!searchPlantillas) return true;
    const q = searchPlantillas.toLowerCase();
    return (p.nombre || '').toLowerCase().includes(q)
      || (p.receptorNombre || p.receptor_nombre || '').toLowerCase().includes(q)
      || (p.receptorIdentificacion || p.receptor_identificacion || '').toLowerCase().includes(q);
  });

  // Selección múltiple - permite seleccionar cualquier factura draft
  const handleSelectDoc = (doc, e) => {
    e?.stopPropagation();
    setSelectedDocs(prev => {
      const isSelected = prev.some(d => d.id === doc.id);
      if (isSelected) {
        return prev.filter(d => d.id !== doc.id);
      } else {
        return [...prev, doc];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedDocs.length === visibleFacturas.length && visibleFacturas.length > 0) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs([...visibleFacturas]);
    }
  };

  const handleVerEditar = (doc, e) => {
    e?.stopPropagation();
    setEditandoDoc(doc);
  };

  const handleEliminarClick = (doc, e) => {
    e?.stopPropagation();
    setEliminarDoc(doc);
  };

  const handleEliminarConfirm = async () => {
    if (!eliminarDoc) return;
    setEliminando(true);
    try {
      await documentsApi.delete(eliminarDoc.id);
      toast.success('Factura eliminada');
      setSelectedDocs(prev => prev.filter(d => d.id !== eliminarDoc.id));
      setEliminarDoc(null);
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    } finally {
      setEliminando(false);
    }
  };

  // Procesa la aprobación secuencialmente y actualiza el modal de progreso
  // factura por factura. Soporta cancelación: si el usuario presiona
  // "Cancelar restantes", marca lo que falta como cancelado y sale.
  const runBulkApprove = async (docs) => {
    bulkApproveCancelRef.current = false;
    const items = docs.map(d => ({
      id: d.id,
      consecutivo: d.numeroConsecutivo || d.consecutivo || d.numero_consecutivo || `#${d.id}`,
      cliente: d.receptor?.nombre || d.receptorNombre || d.receptor_nombre || d.cliente_nombre || '',
      status: 'pending'
    }));
    setBulkApprove({ items, currentIndex: -1, finished: false, cancelled: false });

    for (let i = 0; i < items.length; i++) {
      if (bulkApproveCancelRef.current) {
        setBulkApprove(prev => prev && ({
          ...prev,
          items: prev.items.map((it, idx) =>
            idx >= i && it.status === 'pending' ? { ...it, status: 'cancelled' } : it
          ),
          currentIndex: -1,
          finished: true,
          cancelled: true
        }));
        fetchData();
        return;
      }

      // Marca el actual como "processing"
      setBulkApprove(prev => prev && ({
        ...prev,
        currentIndex: i,
        items: prev.items.map((it, idx) => idx === i ? { ...it, status: 'processing' } : it)
      }));

      try {
        await documentsApi.approveRecurring(items[i].id);
        setBulkApprove(prev => prev && ({
          ...prev,
          items: prev.items.map((it, idx) => idx === i ? { ...it, status: 'success' } : it)
        }));
      } catch (error) {
        const msg = error.response?.data?.error || error.message || 'Error desconocido';
        setBulkApprove(prev => prev && ({
          ...prev,
          items: prev.items.map((it, idx) => idx === i ? { ...it, status: 'error', error: msg } : it)
        }));
      }
    }

    setBulkApprove(prev => prev && ({ ...prev, currentIndex: -1, finished: true }));
    fetchData();
  };

  const handleAprobar = () => {
    const toApprove = selectedDocs.filter(d => !d.metadata?.aprobado_recurrente);
    if (toApprove.length === 0) return;
    runBulkApprove(toApprove);
  };

  const handleRetryBulkApprove = () => {
    if (!bulkApprove) return;
    const failed = bulkApprove.items.filter(i => i.status === 'error');
    if (failed.length === 0) return;
    // Mantenemos los success/cancelled del intento previo en pantalla luego de reintentar:
    // creamos un set de docs nuevos sólo con los fallidos.
    const docs = failed.map(it => ({
      id: it.id,
      numeroConsecutivo: it.consecutivo,
      receptorNombre: it.cliente,
      metadata: {}
    }));
    runBulkApprove(docs);
  };

  const handleCancelBulkApprove = () => {
    bulkApproveCancelRef.current = true;
  };

  const handleCloseBulkApprove = () => {
    setBulkApprove(null);
    setSelectedDocs([]);
  };

  const handleGenerarFacturacion = () => {
    // Filtrar solo los seleccionados que estén pendientes (aprobados)
    const selectedPendientes = selectedDocs.filter(d => d.metadata?.aprobado_recurrente);

    // Si no hay selección, usar todos los pendientes
    const docsToProcess = selectedPendientes.length > 0 ? selectedPendientes : pendientes;

    if (docsToProcess.length === 0) {
      toast.error('No hay facturas pendientes de facturar');
      return;
    }

    // Mostrar modal de confirmación
    setDocsParaProcesar(docsToProcess);
    setShowConfirmModal(true);
  };

  // Las que Hacienda no contestó en los 5 s del lote quedaban «Procesando» para siempre en el modal:
  // se siguen consultando (cada 4 s, hasta 3 min) mientras el modal esté abierto (1-nov-2026).
  useEffect(() => {
    if (!showProgresoModal) return;
    const pendientes = resultados.filter(r => r.success && !r.error && !['aceptado', 'accepted', 'rechazado', 'rejected'].includes(r.estado));
    if (!pendientes.length) return;
    const inicio = Date.now();
    const t = setInterval(async () => {
      if (Date.now() - inicio > 3 * 60 * 1000) { clearInterval(t); return; }
      for (const r of pendientes) {
        try {
          const res = await documentsApi.getById(r.id);
          const d = res.data?.data || res.data;
          const est = d?.estadoHacienda;
          if (est && est !== r.estado && ['aceptado', 'rechazado'].includes(est)) {
            setResultados(prev => prev.map(x => x.id === r.id
              ? { ...x, estado: est, success: est === 'aceptado', error: est === 'rechazado' ? (d.mensajeHacienda || 'Rechazado por Hacienda') : null }
              : x));
          }
        } catch { /* se reintenta en la siguiente vuelta */ }
      }
    }, 4000);
    return () => clearInterval(t);
  }, [showProgresoModal, resultados]);

  const iniciarProcesoFacturacion = () => {
    setShowConfirmModal(false);

    setProgreso(0);
    setTotalProcesar(docsParaProcesar.length);
    setStageInfo(null);
    setResultados([]);
    setCompletado(false);
    setShowProgresoModal(true);

    const ids = docsParaProcesar.map(f => f.id).join(',');
    const token = localStorage.getItem('token');
    const emisorId = currentEmisor?.id;
    const apiUrl = import.meta.env.VITE_API_URL || '';

    eventSourceRef.current = new EventSource(
      `${apiUrl}/internal/recurring/generate-batch?ids=${ids}&token=${encodeURIComponent(token)}&emisor_id=${emisorId}`
    );

    eventSourceRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'start') {
        // Inicio del proceso
        setTotalProcesar(data.total);
      } else if (data.type === 'stage') {
        // Actualización de etapa
        setProgreso(data.current);
        setStageInfo({
          stage: data.stage,
          cliente: data.cliente,
          message: data.message
        });
      } else if (data.type === 'result') {
        // Resultado de una factura
        setResultados(prev => [...prev, data]);
      } else if (data.type === 'complete') {
        // Proceso completado
        setCompletado(true);
        setStageInfo(null);
        eventSourceRef.current?.close();
        setSelectedDocs([]);
        fetchData();
      } else if (data.type === 'error') {
        toast.error(data.error || 'Error en el proceso');
        eventSourceRef.current?.close();
      }
    };

    eventSourceRef.current.onerror = () => {
      eventSourceRef.current?.close();
      if (!completado) {
        toast.error('Error de conexión con el servidor');
        setShowProgresoModal(false);
      }
    };
  };

  if (loading && loadingPlantillas) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col h-[var(--alto-contenido)]">
      {analizarId && <OpsResumenModal documentoId={analizarId} onClose={() => setAnalizarId(null)} />}
      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit shrink-0">
        <button
          onClick={() => handleTabChange('facturas')}
          className={clsx(
            'px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2',
            activeTab === 'facturas'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          )}
        >
          <FileText className="w-4 h-4" />
          Facturas Generadas
          {facturas.length > 0 && (
            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">{facturas.length}</span>
          )}
        </button>
        <button
          onClick={() => handleTabChange('plantillas')}
          className={clsx(
            'px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2',
            activeTab === 'plantillas'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          )}
        >
          <Settings className="w-4 h-4" />
          Plantillas Recurrentes
          {plantillas.length > 0 && (
            <span className="bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full text-xs">{plantillas.length}</span>
          )}
        </button>
      </div>

      {/* Tab: Facturas */}
      {activeTab === 'facturas' && (
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Toolbar */}
          <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 relative shrink-0">
            {/* Search - left */}
            <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchFacturas}
                  onChange={(e) => setSearchFacturas(e.target.value)}
                  placeholder="Buscar por cliente..."
                  className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 w-52 text-sm"
                />
              </div>
            </div>
            {/* Toolbar - center */}
            <div className="flex items-center justify-center gap-1 flex-wrap">
              {selectedDocs.length === 1 && (
                <>
                  <button
                    onClick={() => setPreviewDocId(selectedDocs[0].id)}
                    className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-purple-400 text-purple-600 hover:bg-purple-50 hover:border-purple-500"
                  >
                    <Eye className="h-5 w-5 mb-1" />
                    <span>Ver</span>
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const res = await documentsApi.downloadPdf(selectedDocs[0].id);
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const a = document.createElement('a'); a.href = url;
                        a.download = `FE_${selectedDocs[0].receptor_nombre || 'factura'}.pdf`;
                        a.click(); window.URL.revokeObjectURL(url);
                      } catch (e) { toast.error('Error al descargar PDF'); }
                    }}
                    className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500"
                  >
                    <Download className="h-5 w-5 mb-1" />
                    <span>PDF</span>
                  </button>
                </>
              )}

              <button
                onClick={handleAprobar}
                disabled={selectedDocs.filter(d => !d.metadata?.aprobado_recurrente).length === 0}
                className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-cyan-400 text-cyan-600 hover:bg-cyan-50 hover:border-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Check className="h-5 w-5 mb-1" />
                <span>Aprobar ({selectedDocs.filter(d => !d.metadata?.aprobado_recurrente).length})</span>
              </button>

              <button
                onClick={handleGenerarFacturacion}
                disabled={pendientes.length === 0}
                className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-green-500 text-green-600 hover:bg-green-50 hover:border-green-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Zap className="h-5 w-5 mb-1" />
                <span>Facturar ({selectedDocs.filter(d => d.metadata?.aprobado_recurrente).length || pendientes.length})</span>
              </button>

              <button
                onClick={() => { fetchData(); toast.success('Datos actualizados'); }}
                disabled={loading}
                className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400"
              >
                <RefreshCw className={`h-5 w-5 mb-1 ${loading ? 'animate-spin' : ''}`} />
                <span>Actualizar</span>
              </button>

              {selectedDocs.length > 1 && (
                <button
                  onClick={() => setShowDeleteBatchModal(true)}
                  className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                >
                  <Trash2 className="h-5 w-5 mb-1" />
                  <span>Eliminar ({selectedDocs.length})</span>
                </button>
              )}
            </div>

            {selectedDocs.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-100 text-sm text-gray-500 flex items-center justify-between">
                <div>
                  Seleccionados: <span className="font-medium text-gray-900">{selectedDocs.length} factura(s)</span>
                  <span className="ml-2">- Total: {formatCurrency(selectedDocs.reduce((sum, d) => sum + (d.total_comprobante || d.totalComprobante || 0), 0))}</span>
                </div>
                <button onClick={() => setSelectedDocs([])} className="p-1 hover:bg-gray-100 rounded">
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

      {/* Tabla */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 min-h-0 flex flex-col">
        {/* Sub-tabs: Mensuales vs Anuales */}
        <div className="border-b border-gray-200 shrink-0">
          <nav className="flex gap-4 px-4">
            <button
              onClick={() => { setFacturasSubTab('mensual'); setSelectedDocs([]); }}
              className={`py-2.5 px-1 border-b-2 text-sm font-medium transition-colors ${
                facturasSubTab === 'mensual' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Mensuales
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${facturasSubTab === 'mensual' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                {filteredFacturas.filter(f => {
                  const pid = f.metadata?.recurringInvoiceId;
                  const p = plantillas.find(pl => pl.id === pid);
                  return !p || p.frecuencia !== 'yearly';
                }).length}
              </span>
            </button>
            <button
              onClick={() => { setFacturasSubTab('anual'); setSelectedDocs([]); }}
              className={`py-2.5 px-1 border-b-2 text-sm font-medium transition-colors ${
                facturasSubTab === 'anual' ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Anuales
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${facturasSubTab === 'anual' ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-500'}`}>
                {filteredFacturas.filter(f => {
                  const pid = f.metadata?.recurringInvoiceId;
                  const p = plantillas.find(pl => pl.id === pid);
                  return p && p.frecuencia === 'yearly';
                }).length}
              </span>
            </button>
          </nav>
        </div>
        {facturas.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No hay facturas recurrentes pendientes</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 shadow-[0_1px_0_rgba(229,231,235,1)]">
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-center px-3 py-2 w-10">
                    <input
                      type="checkbox"
                      checked={visibleFacturas.length > 0 && selectedDocs.length === visibleFacturas.length}
                      onChange={handleSelectAll}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      title="Seleccionar todas"
                    />
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Tipo</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Fecha</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700 whitespace-nowrap cursor-pointer hover:bg-gray-100" onClick={() => toggleSortFacturas('cliente')}>
                    Cliente {sortFacturas.field === 'cliente' && (sortFacturas.dir === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700 whitespace-nowrap">SubTotal</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Impuestos</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Total</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Moneda</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Estado</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-700 whitespace-nowrap">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortList(visibleFacturas, sortFacturas, (d) => d.receptor_nombre || d.receptorNombre).map((doc) => {
                  const tipoDoc = doc.tipo_documento || doc.tipoDocumento || '01';
                  const esBorrador = doc.status === 'draft' && !doc.metadata?.aprobado_recurrente;
                  const esPendiente = doc.status === 'draft' && doc.metadata?.aprobado_recurrente;
                  const subtotal = doc.total_venta_neta || doc.totales?.totalVentaNeta || 0;
                  const otrosCargos = parseFloat(doc.total_otros_cargos || doc.totales?.totalOtrosCargos || 0);
                  const impuestos = (parseFloat(doc.total_impuesto || doc.totales?.totalImpuesto || 0)) + otrosCargos;
                  const total = doc.total_comprobante || doc.totales?.totalComprobante || 0;
                  const moneda = doc.codigo_moneda || doc.codigoMoneda || 'CRC';
                  const isSelected = selectedDocs.some(d => d.id === doc.id);

                  const statusKey = esBorrador ? 'draft' : esPendiente ? 'pendiente' : doc.status;
                  const StatusIcon = STATUS_CONFIG[statusKey]?.icon || Clock;

                  return (
                    <tr
                      key={doc.id}
                      className={clsx(
                        "transition-colors",
                        isSelected ? "bg-blue-50 hover:bg-blue-100" : "hover:bg-gray-50"
                      )}
                    >
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectDoc(doc, e)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                          {TIPO_DOC[tipoDoc] || tipoDoc}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {dayjs(doc.fecha_emision || doc.created_at).format('DD/MM/YYYY')}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">
                          {doc.receptor_nombre || doc.receptorNombre || doc.metadata?.recurringNombre || 'Sin cliente'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {doc.receptor_identificacion || doc.receptorIdentificacion || (doc.metadata?.recurringInvoiceId ? `Plantilla #${doc.metadata.recurringInvoiceId}` : '')}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm text-right text-gray-700">
                        {formatCurrency(subtotal, moneda)}
                      </td>
                      <td className="px-3 py-2 text-sm text-right text-gray-700">
                        {formatCurrency(impuestos, moneda)}
                      </td>
                      <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">
                        {formatCurrency(total, moneda)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-medium">{moneda}</span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={clsx(
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                          STATUS_CONFIG[statusKey]?.color || 'bg-gray-100 text-gray-700'
                        )}>
                          <StatusIcon className="h-3 w-3" />
                          {STATUS_CONFIG[statusKey]?.label || doc.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {/* Los botones quedan alineados fila con fila: el de Ops reserva su lugar aunque no aplique */}
                        <div className="flex items-center justify-end gap-1">
                          {conOps && (
                            <button
                              disabled={!deConsumo.has(doc.id)} aria-hidden={!deConsumo.has(doc.id)}
                              onClick={(e) => { e.stopPropagation(); setAnalizarId(doc.id); }}
                              title="Ver los cambios que Finance360 Ops le hizo a esta FE"
                              className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 rounded-lg border-[1.5px] border-indigo-500 whitespace-nowrap ${deConsumo.has(doc.id) ? '' : 'invisible'}`}
                            >
                              <Gauge className="w-3.5 h-3.5" />
                              FinOps <Check className="w-3.5 h-3.5 text-emerald-600" />
                            </button>
                          )}
                          <button
                            onClick={(e) => handleVerEditar(doc, e)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-600 hover:bg-purple-50 rounded-lg border border-purple-300"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            Ver
                          </button>
                          <button
                            onClick={(e) => handleEliminarClick(doc, e)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg border border-red-300"
                          >
                            <Delete className="h-5 w-5 text-red-500" />
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Footer Totals — sticky al pie del scroll. Columnas:
                  [checkbox, Tipo, Fecha, Cliente] | SubTotal | Impuestos | Total | [Moneda, Estado, Acciones] */}
              <tfoot className="sticky bottom-0 z-10 bg-gray-50 border-t-2 border-gray-300 shadow-[0_-1px_0_rgba(156,163,175,1)]">
                {['CRC', 'USD'].map(moneda => {
                  const items = visibleFacturas.filter(f => (f.codigo_moneda || f.codigoMoneda || 'CRC') === moneda);
                  if (items.length === 0) return null;
                  const sym = moneda === 'USD' ? '$' : '₡';
                  const totSub = items.reduce((s, f) => s + parseFloat(f.total_venta_neta || f.totales?.totalVentaNeta || 0), 0);
                  const totImp = items.reduce((s, f) => s + parseFloat(f.total_impuesto || f.totales?.totalImpuesto || 0) + parseFloat(f.total_otros_cargos || f.totales?.totalOtrosCargos || 0), 0);
                  const totTotal = items.reduce((s, f) => s + parseFloat(f.total_comprobante || f.totales?.totalComprobante || 0), 0);
                  return (
                    <tr key={moneda} className="font-semibold text-gray-800">
                      <td colSpan={4} className="px-3 py-2 text-right">Totales {moneda}:</td>
                      <td className="px-3 py-2 text-right">{sym}{totSub.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2 text-right">{sym}{totImp.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2 text-right">{sym}{totTotal.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td colSpan={3}></td>
                    </tr>
                  );
                })}
              </tfoot>
            </table>
          </div>
        )}
      </div>
        </div>
      )}

      {/* Tab: Plantillas */}
      {activeTab === 'plantillas' && (
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Toolbar Plantillas */}
          <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 relative shrink-0">
            {/* Search - left */}
            <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchPlantillas}
                  onChange={(e) => setSearchPlantillas(e.target.value)}
                  placeholder="Buscar por nombre o cliente..."
                  className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 w-52 text-sm"
                />
              </div>
            </div>
            {/* Toolbar - center */}
            <div className="flex items-center justify-center gap-1 flex-wrap">
              {selectedPlantillas.length === 1 && (
                <button
                  onClick={() => setPreviewPlantillaId(selectedPlantillas[0].id)}
                  className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-purple-400 text-purple-600 hover:bg-purple-50 hover:border-purple-500"
                >
                  <Eye className="h-5 w-5 mb-1" />
                  <span>Ver</span>
                </button>
              )}

              <button
                onClick={() => setShowCrearPlantilla(true)}
                className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-cyan-400 text-cyan-600 hover:bg-cyan-50 hover:border-cyan-500"
              >
                <Plus className="h-5 w-5 mb-1" />
                <span>Nueva Plantilla</span>
              </button>

              <button
                onClick={fetchPlantillas}
                className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400"
              >
                <RefreshCw className="h-5 w-5 mb-1" />
                <span>Actualizar</span>
              </button>

              <button
                onClick={() => setShowReporteConfig(true)}
                className="flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px] border border-violet-400 text-violet-600 hover:bg-violet-50 hover:border-violet-500"
                title="Configurar destinatarios del reporte post-ciclo"
              >
                <Settings className="h-5 w-5 mb-1" />
                <span>Notificaciones</span>
              </button>
            </div>
          </div>

          {/* Tabla de Plantillas */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 min-h-0 flex flex-col">
            {/* Sub-tabs: Mensuales vs Anuales */}
            <div className="border-b border-gray-200 shrink-0">
              <nav className="flex gap-4 px-4">
                <button
                  onClick={() => setPlantillaSubTab('mensual')}
                  className={`py-2.5 px-1 border-b-2 text-sm font-medium transition-colors ${
                    plantillaSubTab === 'mensual' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Mensuales
                  <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${plantillaSubTab === 'mensual' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                    {plantillas.filter(p => p.frecuencia !== 'yearly').length}
                  </span>
                </button>
                <button
                  onClick={() => setPlantillaSubTab('anual')}
                  className={`py-2.5 px-1 border-b-2 text-sm font-medium transition-colors ${
                    plantillaSubTab === 'anual' ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Anuales
                  <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${plantillaSubTab === 'anual' ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-500'}`}>
                    {plantillas.filter(p => p.frecuencia === 'yearly').length}
                  </span>
                </button>
              </nav>
            </div>
            {loadingPlantillas ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <div className="animate-spin h-8 w-8 border-4 border-cyan-600 border-t-transparent rounded-full mx-auto"></div>
                <p className="mt-2 text-gray-500">Cargando plantillas...</p>
              </div>
            ) : plantillas.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <Repeat className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No hay plantillas recurrentes configuradas</p>
                <p className="text-sm text-gray-400 mt-2">
                  Crea una nueva plantilla para generar facturas automáticamente
                </p>
                <button
                  onClick={() => setShowCrearPlantilla(true)}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                >
                  <Plus className="w-4 h-4" />
                  Crear Plantilla
                </button>
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-gray-50 shadow-[0_1px_0_rgba(229,231,235,1)]">
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-3 py-2 w-8">
                        <input
                          type="checkbox"
                          checked={filteredPlantillas.length > 0 && selectedPlantillas.length === filteredPlantillas.length}
                          onChange={() => {
                            if (selectedPlantillas.length === filteredPlantillas.length) setSelectedPlantillas([]);
                            else setSelectedPlantillas([...filteredPlantillas]);
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 cursor-pointer"
                        />
                      </th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => toggleSortPlantillas('nombre')}>
                        Nombre {sortPlantillas.field === 'nombre' && (sortPlantillas.dir === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => toggleSortPlantillas('cliente')}>
                        Cliente {sortPlantillas.field === 'cliente' && (sortPlantillas.dir === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="text-right px-3 py-2 font-medium text-gray-700">Subtotal</th>
                      <th className="text-right px-3 py-2 font-medium text-gray-700">Impuesto</th>
                      <th className="text-right px-3 py-2 font-medium text-gray-700">Total</th>
                      <th className="text-center px-3 py-2 font-medium text-gray-700">Moneda</th>
                      <th className="text-center px-3 py-2 font-medium text-gray-700">Frecuencia</th>
                      <th className="text-center px-3 py-2 font-medium text-gray-700">Días</th>
                      <th className="text-center px-3 py-2 font-medium text-gray-700">Próxima</th>
                      <th className="text-center px-3 py-2 font-medium text-gray-700">Generadas</th>
                      <th className="text-center px-3 py-2 font-medium text-gray-700">Estado</th>
                      <th className="text-center px-3 py-2 font-medium text-gray-700">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortList(filteredPlantillas.filter(p => plantillaSubTab === 'anual' ? p.frecuencia === 'yearly' : p.frecuencia !== 'yearly'), sortPlantillas, (p, f) => f === 'nombre' ? p.nombre : p.receptorNombre || p.receptor_nombre).map((plantilla) => (
                      <tr key={plantilla.id} className={clsx("transition-colors", selectedPlantillas.some(p => p.id === plantilla.id) ? "bg-blue-50 hover:bg-blue-100" : "hover:bg-gray-50")}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedPlantillas.some(p => p.id === plantilla.id)}
                            onChange={() => {
                              setSelectedPlantillas(prev =>
                                prev.some(p => p.id === plantilla.id)
                                  ? prev.filter(p => p.id !== plantilla.id)
                                  : [...prev, plantilla]
                              );
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 cursor-pointer"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">{plantilla.nombre}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-gray-900">{plantilla.receptorNombre}</div>
                          <div className="text-xs text-gray-500">{plantilla.receptorIdentificacion}</div>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700">
                          {formatCurrency(plantilla.subtotal || 0)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700">
                          <div className="flex items-center justify-end gap-1">
                            {plantilla.receptorExonerado && (
                              <span className="relative flex h-2.5 w-2.5" title="Cliente Exonerado de IVA">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-purple-500"></span>
                              </span>
                            )}
                            {formatCurrency(plantilla.totalImpuesto || 0)}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-gray-900">
                          {formatCurrency(plantilla.total || 0)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-medium">{plantilla.codigoMoneda || 'CRC'}</span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            <Calendar className="w-3 h-3" />
                            {FRECUENCIA_LABELS[plantilla.frecuencia] || plantilla.frecuencia}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className="text-xs text-gray-600 font-mono">
                            {(plantilla.diasGeneracion || []).join(', ')}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center text-gray-600">
                          {plantilla.proximaGeneracion
                            ? dayjs(plantilla.proximaGeneracion).format('DD/MM/YYYY')
                            : '-'}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className="font-mono text-gray-700">{plantilla.totalGeneradas || 0}</span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => handleTogglePlantilla(plantilla)}
                            className={clsx(
                              'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors',
                              plantilla.isActive
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            )}
                          >
                            {plantilla.isActive ? (
                              <>
                                <ToggleRight className="w-3.5 h-3.5" />
                                Activa
                              </>
                            ) : (
                              <>
                                <ToggleLeft className="w-3.5 h-3.5" />
                                Inactiva
                              </>
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleGenerarManual(plantilla)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-600 hover:bg-green-50 rounded-lg border border-green-300"
                              title="Generar factura borrador ahora"
                            >
                              <Zap className="w-3.5 h-3.5" />
                              Generar
                            </button>
                            <button
                              onClick={() => setEditandoPlantilla(plantilla)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-300"
                            >
                              <Pencil className="h-5 w-5 text-blue-500" />
                              Editar
                            </button>
                            <button
                              onClick={() => setEliminarPlantilla(plantilla)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg border border-red-300"
                            >
                              <Delete className="h-5 w-5 text-red-500" />
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Footer Totals — sticky. Columnas:
                      [checkbox, Nombre, Cliente] | Subtotal | Impuesto | Total | [Moneda, Frecuencia, Días, Próxima, Generadas, Estado, Acciones] */}
                  <tfoot className="sticky bottom-0 z-10 bg-gray-50 border-t-2 border-gray-300 shadow-[0_-1px_0_rgba(156,163,175,1)]">
                    {['CRC', 'USD'].map(moneda => {
                      const items = filteredPlantillas.filter(p =>
                        (plantillaSubTab === 'anual' ? p.frecuencia === 'yearly' : p.frecuencia !== 'yearly') &&
                        (p.codigoMoneda || 'CRC') === moneda
                      );
                      if (items.length === 0) return null;
                      const sym = moneda === 'USD' ? '$' : '₡';
                      const totSub = items.reduce((s, p) => s + (p.subtotal || 0), 0);
                      const totImp = items.reduce((s, p) => s + (p.totalImpuesto || 0), 0);
                      const totTotal = items.reduce((s, p) => s + (p.total || 0), 0);
                      return (
                        <tr key={moneda} className="font-semibold text-gray-800">
                          <td colSpan={3} className="px-3 py-2 text-right">Totales {moneda}:</td>
                          <td className="px-3 py-2 text-right">{sym}{totSub.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="px-3 py-2 text-right">{sym}{totImp.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="px-3 py-2 text-right">{sym}{totTotal.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td colSpan={7}></td>
                        </tr>
                      );
                    })}
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de edición de documento - Usa el mismo modal que plantillas pero sin config de recurrencia */}
      {editandoDoc && (
        <CrearPlantillaRecurrenteModal
          documento={editandoDoc}
          onClose={() => setEditandoDoc(null)}
          onSave={() => {
            setEditandoDoc(null);
            fetchData();
          }}
        />
      )}

      {/* Modal de confirmación eliminar */}
      <ConfirmarModal
        isOpen={!!eliminarDoc}
        onClose={() => setEliminarDoc(null)}
        onConfirm={handleEliminarConfirm}
        titulo="Eliminar Factura"
        mensaje={`¿Está seguro que desea eliminar la factura de ${eliminarDoc?.receptor_nombre || 'este cliente'}? Esta acción no se puede deshacer.`}
        loading={eliminando}
      />

      {/* Modal eliminar masivo */}
      {showDeleteBatchModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Eliminar Facturas</h2>
            <p className="text-gray-600 mb-4">
              ¿Está seguro que desea eliminar <strong>{selectedDocs.length} facturas</strong> seleccionadas? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteBatchModal(false)}
                className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={async () => {
                setShowDeleteBatchModal(false);
                let deleted = 0;
                for (const doc of selectedDocs) {
                  try { await documentsApi.delete(doc.id); deleted++; } catch (e) { /* skip */ }
                }
                toast.success(`${deleted} facturas eliminadas`);
                setSelectedDocs([]);
                fetchData();
              }}
                className="px-4 py-2 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50">
                Eliminar ({selectedDocs.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación antes de facturar */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <div className="fixed inset-0 bg-gray-900/60" onClick={() => setShowConfirmModal(false)} />
            <div className="relative w-full max-w-lg bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden">
              {/* Header - outline style */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg border border-amber-300 bg-amber-50">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Confirmar Facturación</h3>
                    <p className="text-sm text-gray-500">{docsParaProcesar.length} factura(s) seleccionada(s)</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-700 mb-4">
                  Este proceso enviará <span className="font-semibold text-gray-900">{docsParaProcesar.length} factura(s) electrónica(s)</span> a
                  Hacienda para su validación y registro oficial.
                </p>
                <div className="border border-amber-300 rounded-lg p-4 mb-6">
                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-gray-700">
                      <p className="font-medium text-gray-900 mb-1">Importante:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-600">
                        <li>Se asignarán consecutivos oficiales a cada factura</li>
                        <li>Las facturas aceptadas no pueden ser modificadas</li>
                        <li>Se enviarán correos automáticos a los clientes</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-6">¿Está seguro que desea proceder con la facturación?</p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="px-5 py-2.5 text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={iniciarProcesoFacturacion}
                    className="px-5 py-2.5 text-green-600 font-medium rounded-lg border border-green-500 hover:bg-green-50 transition-colors"
                  >
                    Sí, Facturar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de progreso */}
      <ProgresoModal
        isOpen={showProgresoModal}
        onClose={() => setShowProgresoModal(false)}
        resultados={resultados}
        progreso={progreso}
        total={totalProcesar}
        stage={stageInfo}
        completado={completado}
        onErrorClick={(r) => setMensajeHaciendaModal({ mensaje: r.error || 'Error desconocido', doc: r })}
      />

      {/* Modal eliminar plantilla */}
      <ConfirmarModal
        isOpen={!!eliminarPlantilla}
        onClose={() => setEliminarPlantilla(null)}
        onConfirm={handleEliminarPlantillaConfirm}
        titulo="Eliminar Plantilla Recurrente"
        mensaje={`¿Está seguro que desea eliminar la plantilla "${eliminarPlantilla?.nombre}"? Ya no se generarán facturas automáticamente para ${eliminarPlantilla?.receptorNombre}.`}
        loading={eliminandoPlantilla}
      />

      {/* Modal editar plantilla - Pantalla completa como CrearFactura */}
      {editandoPlantilla && (
        <CrearPlantillaRecurrenteModal
          plantilla={editandoPlantilla}
          onClose={() => setEditandoPlantilla(null)}
          onSave={() => {
            setEditandoPlantilla(null);
            fetchPlantillas();
          }}
        />
      )}

      {/* Modal Mensaje Hacienda (errores/rechazos) */}
      {mensajeHaciendaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setMensajeHaciendaModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b bg-red-50 rounded-t-xl">
              <h3 className="text-lg font-semibold text-red-800 flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Respuesta de Hacienda
              </h3>
              <button onClick={() => setMensajeHaciendaModal(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-4">
              {mensajeHaciendaModal.doc?.receptorNombre && (
                <div className="mb-3 text-sm text-gray-600">
                  <strong>Cliente:</strong> {mensajeHaciendaModal.doc.receptorNombre}
                </div>
              )}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                <pre className="text-sm text-red-800 whitespace-pre-wrap font-mono select-text">
                  {mensajeHaciendaModal.mensaje}
                </pre>
              </div>
              <p className="mt-2 text-xs text-gray-500">Puedes seleccionar el texto para copiarlo</p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(mensajeHaciendaModal.mensaje);
                  toast.success('Mensaje copiado');
                }}
                className="px-4 py-2 text-sm border border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50"
              >
                Copiar Mensaje
              </button>
              <button
                onClick={() => setMensajeHaciendaModal(null)}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal preview */}
      {(previewDocId || previewPlantillaId) && (
        <PreviewFacturaModal
          docId={previewDocId}
          plantillaId={previewPlantillaId}
          emisor={currentEmisor}
          onClose={() => { setPreviewDocId(null); setPreviewPlantillaId(null); }}
        />
      )}

      {/* Modal crear plantilla - Pantalla completa como CrearFactura */}
      {showCrearPlantilla && (
        <CrearPlantillaRecurrenteModal
          onClose={() => setShowCrearPlantilla(false)}
          onSave={() => {
            setShowCrearPlantilla(false);
            fetchPlantillas();
          }}
        />
      )}

      {/* Modal: configuración de destinatarios del reporte post-ciclo */}
      {showReporteConfig && (
        <ReporteRecurrenteConfigModal onClose={() => setShowReporteConfig(false)} />
      )}

      {/* Modal: progreso de aprobación masiva */}
      {bulkApprove && (
        <BulkApproveProgressModal
          data={bulkApprove}
          onCancel={handleCancelBulkApprove}
          onClose={handleCloseBulkApprove}
          onRetry={handleRetryBulkApprove}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// BulkApproveProgressModal — progreso en tiempo real de aprobación masiva
// ══════════════════════════════════════════════════════════════════════
function BulkApproveProgressModal({ data, onCancel, onClose, onRetry }) {
  const { items, currentIndex, finished, cancelled } = data;
  const total = items.length;
  const successCount = items.filter(i => i.status === 'success').length;
  const errorCount = items.filter(i => i.status === 'error').length;
  const cancelledCount = items.filter(i => i.status === 'cancelled').length;
  const processedCount = successCount + errorCount;
  const pct = total > 0 ? Math.round((processedCount / total) * 100) : 0;

  const statusIcon = (status) => {
    if (status === 'pending') return <Clock className="h-4 w-4 text-gray-300" />;
    if (status === 'processing') return <Loader2 className="h-4 w-4 text-cyan-600 animate-spin" />;
    if (status === 'success') return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (status === 'error') return <XCircle className="h-4 w-4 text-red-600" />;
    if (status === 'cancelled') return <X className="h-4 w-4 text-gray-400" />;
    return null;
  };
  const statusText = (it) => {
    if (it.status === 'pending') return 'En cola';
    if (it.status === 'processing') return 'Enviando a Hacienda…';
    if (it.status === 'success') return 'Aceptada';
    if (it.status === 'error') return it.error || 'Error';
    if (it.status === 'cancelled') return 'Cancelada';
    return '';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-cyan-600 to-blue-700 rounded-t-xl shrink-0">
          <h3 className="font-semibold text-white text-lg flex items-center gap-2">
            {finished
              ? (cancelled
                  ? <><X className="h-5 w-5" /> Aprobación cancelada</>
                  : (errorCount === 0
                      ? <><CheckCircle className="h-5 w-5" /> {successCount > 0 ? '¡Todas aprobadas!' : 'Sin cambios'}</>
                      : <><AlertTriangle className="h-5 w-5" /> Aprobación completada con errores</>))
              : <><Loader2 className="h-5 w-5 animate-spin" /> Aprobando {total} factura{total !== 1 ? 's' : ''}</>}
          </h3>
        </div>

        {/* Barra de progreso o resumen */}
        <div className="px-6 pt-4 pb-2 shrink-0">
          {!finished ? (
            <>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-700 font-medium">
                  Procesando {Math.min(currentIndex + 1, total)} de {total}
                </span>
                <span className="text-cyan-600 font-bold">{pct}%</span>
              </div>
              <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 transition-all duration-300" style={{ width: `${pct}%` }} />
              </div>
            </>
          ) : (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="text-2xl font-bold text-green-700">{successCount}</div>
                <div className="text-xs text-green-700 font-medium">Aceptadas</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-2xl font-bold text-red-700">{errorCount}</div>
                <div className="text-xs text-red-700 font-medium">Rechazadas</div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="text-2xl font-bold text-gray-700">{cancelledCount}</div>
                <div className="text-xs text-gray-700 font-medium">Canceladas</div>
              </div>
            </div>
          )}
        </div>

        {/* Lista de facturas */}
        <div className="flex-1 overflow-y-auto px-6 py-3 min-h-0">
          <div className="space-y-1">
            {items.map((it, idx) => (
              <div
                key={`${it.id}-${idx}`}
                className={`flex items-start gap-3 px-3 py-2 rounded-lg text-sm border ${
                  it.status === 'processing' ? 'bg-cyan-50 border-cyan-200' :
                  it.status === 'error' ? 'bg-red-50 border-red-100' :
                  it.status === 'success' ? 'bg-white border-gray-100' :
                  it.status === 'cancelled' ? 'bg-gray-50 border-gray-100' :
                  'bg-gray-50 border-gray-100'
                }`}
              >
                <div className="mt-0.5 shrink-0">{statusIcon(it.status)}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-gray-800 truncate" title={it.consecutivo}>{it.consecutivo}</div>
                  {it.cliente && <div className="text-xs text-gray-500 truncate" title={it.cliente}>{it.cliente}</div>}
                  {it.status === 'error' && it.error && (
                    <div className="text-[11px] text-red-700 mt-1 break-words">{it.error}</div>
                  )}
                </div>
                <div className={`text-xs whitespace-nowrap shrink-0 ${
                  it.status === 'success' ? 'text-green-700 font-medium' :
                  it.status === 'error' ? 'text-red-700 font-medium' :
                  it.status === 'processing' ? 'text-cyan-700 font-medium' :
                  'text-gray-500'
                }`}>
                  {it.status === 'error' ? 'Rechazada' : statusText(it)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl shrink-0 flex items-center justify-end gap-3">
          {!finished ? (
            <button
              onClick={onCancel}
              className="px-5 py-2 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium"
            >
              Cancelar restantes
            </button>
          ) : (
            <>
              {errorCount > 0 && (
                <button
                  onClick={onRetry}
                  className="px-5 py-2 border-2 border-amber-400 text-amber-700 rounded-lg hover:bg-amber-50 text-sm font-semibold flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" /> Reintentar fallidas ({errorCount})
                </button>
              )}
              <button
                onClick={onClose}
                className="px-6 py-2 border-2 border-cyan-500 text-cyan-600 rounded-lg hover:bg-cyan-50 text-sm font-semibold"
              >
                Cerrar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ReporteRecurrenteConfigModal — destinatarios del reporte post-ciclo
// ══════════════════════════════════════════════════════════════════════
function ReporteRecurrenteConfigModal({ onClose }) {
  const [recipients, setRecipients] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await recurringApi.getReporteConfig();
        const d = res.data?.data;
        if (d) {
          setRecipients(d.recipient_emails || '');
          setIsActive(d.is_active !== 0 && d.is_active !== false);
        }
      } catch (e) {
        /* sin config aún */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const guardar = async () => {
    setSaving(true);
    try {
      await recurringApi.saveReporteConfig({ recipientEmails: recipients, isActive });
      toast.success('Configuración guardada');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const enviarPrueba = async () => {
    // Guarda primero para usar los destinatarios actuales
    setSendingTest(true);
    try {
      await recurringApi.saveReporteConfig({ recipientEmails: recipients, isActive: true });
      await recurringApi.sendReporteTest();
      toast.success('Reporte de prueba enviado');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al enviar prueba');
    } finally {
      setSendingTest(false);
    }
  };

  const count = recipients.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl mx-4 max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-violet-600 to-purple-700 rounded-t-xl shrink-0">
          <h3 className="font-semibold text-white flex items-center gap-2 text-lg">
            <Mail className="h-5 w-5" /> Notificaciones — FE Recurrentes
          </h3>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            Cada vez que se complete un <strong>ciclo de generación</strong> de FE recurrentes, se enviará un correo con el reporte de las facturas generadas a los destinatarios indicados aquí. El reporte indica que las facturas están disponibles en <em>Facturación Recurrente → Facturas Generadas</em> para su revisión y aprobación.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Destinatarios <span className="text-xs text-gray-400 font-normal">(separados por coma)</span>
            </label>
            <textarea
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              rows={3}
              placeholder="cobros@empresa.com, gerencia@empresa.com"
              disabled={loading}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 resize-none"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              {count > 0 ? `${count} destinatario${count !== 1 ? 's' : ''}` : 'Sin destinatarios'} · El correo se envía desde la cuenta transaccional del emisor.
            </p>
          </div>

          <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${isActive ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="accent-green-600 w-4 h-4" />
            Notificaciones activas
          </label>
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl shrink-0">
          <button
            onClick={enviarPrueba}
            disabled={sendingTest || saving || count === 0}
            className="px-4 py-2 border-2 border-blue-400 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 disabled:opacity-50 flex items-center gap-2"
            title="Envía un reporte de prueba con el historial reciente"
          >
            {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Enviar prueba
          </button>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-5 py-2 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">Cancelar</button>
            <button onClick={guardar} disabled={saving || sendingTest}
              className="px-6 py-2 border-2 border-violet-500 text-violet-600 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50 hover:bg-violet-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
